import { useCursorStyle } from '@/context/cursorStyleContext'
import { useKeyBindings } from '@/hooks/useKeyBindings'
import { useEffect, useRef, useState } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/shadcn/tooltip'
import { Input } from '@/components/ui/shadcn/input'

interface Props {
  value: number
  onChange: (value: number, isHoverSelection?: boolean) => void
  onBlur: (value: number) => void
  step: number
  min: number
  max: number
  hoverContent?: string
}

export function NumberInput({ value, onChange, onBlur, step, min, max, hoverContent }: Props) {
  const { startDragging, stopDragging } = useCursorStyle()
  const formatValue = (val: number) => (step % 1 !== 0 ? val.toFixed(2) : val.toString())
  const [inputValue, setInputValue] = useState(formatValue(value))
  const [isFocused, setIsFocused] = useState(false)
  const isDraggingRef = useRef(false)
  const startXRef = useRef(0)
  const currentValueRef = useRef(value)

  useKeyBindings(isFocused, {
    enter: () => handleBlur(),
  })

  const handleBlur = () => {
    if (inputValue === '' || isNaN(Number(inputValue))) {
      setInputValue(formatValue(value))
      return
    }

    const newValue = Number(inputValue)
    if (newValue >= min && newValue <= max) {
      setInputValue(formatValue(newValue))
      onBlur(newValue)
    } else {
      setInputValue(formatValue(value))
    }
  }

  const doubleClickTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const handleMouseDown = (e: React.MouseEvent) => {
    if (doubleClickTimeoutRef.current) {
      clearTimeout(doubleClickTimeoutRef.current)
      doubleClickTimeoutRef.current = null
      return
    }

    doubleClickTimeoutRef.current = setTimeout(() => {
      doubleClickTimeoutRef.current = null
    }, 200)

    e.preventDefault()

    startDragging('right')
    isDraggingRef.current = true
    startXRef.current = e.clientX
    currentValueRef.current = value

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDraggingRef.current) return

    e.preventDefault()

    const deltaX = e.clientX - startXRef.current
    const sensitivity = 1
    const steps = Math.round((deltaX * sensitivity) / 5)

    if (steps !== 0) {
      const newValue = Math.min(max, Math.max(min, currentValueRef.current + steps * step))
      currentValueRef.current = newValue
      onChange(newValue, true)
      setInputValue(formatValue(newValue))
      startXRef.current = e.clientX
    }
  }

  const handleMouseUp = (e: MouseEvent) => {
    e.preventDefault()
    stopDragging()
    isDraggingRef.current = false
    onChange(currentValueRef.current, false)
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  const inputStyle = {
    cursor: isFocused ? 'text' : isDraggingRef ? 'ew-resize' : 'default',
  }

  useEffect(() => {
    currentValueRef.current = value
  }, [value])

  return (
    <Tooltip>
      <div className="flex items-center">
        <TooltipTrigger>
          <Input
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleBlur}
            onFocus={() => setIsFocused(true)}
            onMouseDown={handleMouseDown}
            style={inputStyle}
            className="text-foreground border-border h-7 w-14 items-center border text-center text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
        </TooltipTrigger>
      </div>
      <TooltipContent className="top-1 w-full" hidden={hoverContent === undefined}>
        <p>{hoverContent}</p>
      </TooltipContent>
    </Tooltip>
  )
}
